import { type JanusCtx, type Row, handBack, insertRoll } from '../context.js';
import { type JanusInput } from '../janus.js';
import { postEffectConditions } from '../post-effect-conditions.js';
import { rollDiceFaces } from './roll-dice-arr.js';
import { upcastRoll } from '../upcast.js';
import { SUMMON_ROLL } from '../summon.js';
import { effectTargets } from '../is-target-valid-for-effect.js';
import { variableDice } from '../variable-amount.js';
import { checkScalableCantrip } from '../check-scalable-cantrip.js';
import { rollTypeRollStacks, rollTypeVariable } from './roll-type-roll-shared.js';

/**
 * includes/functions/janus/rolls/summon_roll.php:summon_roll (Wizard v9 WP3, 2026-10-09) — the plain Summon Roll: dice × die + mod creatures
 * (a dice-less roll summons exactly its mod — Summon Skeletal Minions: 3), then post_effect_conditions summons that many (summon.ts), then
 * the summon_roll row. Shared tag and ADV/DIS readers settle inputs before drawing; status hints/use-up and roll-trigger firing stay named.
 */
export function summonRoll(ctx: JanusCtx, input: JanusInput, ability: Row, caster: Row, targets: Row[]): void {
  const eligible = effectTargets(ctx, input, ability, caster, targets);
  if (!eligible) return;
  if (targets.length !== 1) handBack('summon_roll:multi-target');
  const baseDice = rollTypeVariable(ctx, input, caster, variableDice(ctx.read.value(ability, 'dice'), input.useAbilityVariable1), 'summon_roll');
  const baseMod = rollTypeVariable(ctx, input, caster, ctx.read.value(ability, 'mod'), 'summon_roll');
  const up = upcastRoll(ctx, ability, baseDice, baseMod);
  const dice = checkScalableCantrip(ctx, ability, caster, up.dice);
  const die = rollTypeVariable(ctx, input, caster, ctx.read.value(ability, 'die'), 'summon_roll');
  const target = [...eligible][0]!;
  const advDisStacks = rollTypeRollStacks(ctx, ability, caster, target, SUMMON_ROLL, 'summon_roll');
  const rolled = rollDiceFaces(ctx, dice, die, advDisStacks);
  const result = rolled.total + up.mod;
  // post_effect_conditions(): the summon_roll loop raises `roll_result` creatures (MUST SUMMON SOMETHING: none below 1)
  const prior = ctx.summonRollResult;
  ctx.summonRollResult = result;
  try { postEffectConditions(ctx, input, ability, caster, target); } finally { ctx.summonRollResult = prior; }
  insertRoll(ctx, { roll_type_1: SUMMON_ROLL, roll_type_2: '', roll_item_id: input.abilityId, by: caster, to: target, roll_result: result,
    rolls_full: rolled.faces, dice, die, mod: up.mod, adv_dis_stacks: advDisStacks, dc: 0 });
}
